/* eslint-disable @typescript-eslint/consistent-type-assertions */

import { assign, setup } from 'xstate5';

import { log } from './helpers';
import { downloadAsset } from './remote-asset.actors';
import { type Context, type Event, type Input } from './remote-asset.types';

// ─── Setup ───────────────────────────────────────────────────────────────────

const machineSetup = setup({
  types: {
    context: {} as Context,
    events: {} as Event,
    input: {} as Input,
  },
  actors: {
    downloadAsset,
  },
  actions: {
    storeLocalURI: assign((_, params: StoreLocalURIParams) => {
      log.debug('Resolved asset', { localURI: params.localURI });

      return { localURI: params.localURI };
    }),
    storeError: assign((_, params: StoreErrorParams) => {
      log.error('Failed to download asset', { error: params.error });

      return { error: params.error };
    }),
    resetContext: assign({ localURI: undefined, error: undefined }),
    resetError: assign({ error: undefined }),
  },
});

// ─── Machine ─────────────────────────────────────────────────────────────────

export const remoteAssetMachine = machineSetup.createMachine({
  id: 'remoteAsset',
  description:
    'A simple machine to download a remote asset and store it locally.',
  initial: 'downloading',
  context: (args) => ({
    url: args.input.url,

    localURI: undefined,
    error: undefined,
  }),
  states: {
    downloading: {
      entry: 'resetContext',
      invoke: {
        src: 'downloadAsset',
        input: ({ context }) => ({ url: context.url }),
        onDone: {
          target: 'downloaded',
          actions: [
            {
              type: 'storeLocalURI',
              params: ({ event }) => ({ localURI: event.output.localURI }),
            },
          ],
        },
        onError: {
          target: 'failure',
          actions: [
            {
              type: 'storeError',
              params: ({ event }) => ({ error: event.error as Error }),
            },
          ],
        },
      },
    },
    failure: {
      on: {
        retry: 'downloading',
      },
    },
    downloaded: {
      entry: 'resetError',
      type: 'final',
    },
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type StoreLocalURIParams = {
  localURI: string;
};

type StoreErrorParams = {
  error: Error;
};
